import React from 'react';
import styled, { useTheme } from 'styled-components/macro';
import { theme } from 'lib/style';
import Select, { components, OptionProps } from 'react-select';
import {
  getCountries,
  getCountryCallingCode,
  isSupportedCountry,
} from 'react-phone-number-input/input';
import en from 'react-phone-number-input/locale/en.json';
import ReactCountryFlag from 'react-country-flag';
import { CountryCode } from 'app/pages/admin/users/components/userDetails/ContactSection';

interface PhoneNumberInputProps {
  setExtension: (ext: CountryCode) => void;
  ext?: string;
}

const OptionWrapper = styled.div`
  width: 100%;
  min-width: 120px;
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;

  justify-content: space-between;
  align-items: center;
`;

const CountryCodeWrapper = styled.div`
  width: 100%;
  overflow: hidden;
  padding-left: 8px;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: ${theme.palette.covideoBlue100};
`;

const CountryName = styled.div`
  width: 100%;
  overflow: hidden;
  text-align: right;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;

  color: ${theme.palette.covideoBlue40};
`;

const OptionIconWrapper = styled.div`
  display: flex;
  text-align: center;
`;

type CountryOption = {
  value: string;
  label: string;
};

const CustomOption = (props: OptionProps<CountryOption, false>) => {
  const { data } = props;
  const country = getCountries().find(country => country === data.value);
  if (!country) {
    return <></>;
  }

  return (
    <components.Option {...props}>
      <OptionWrapper>
        <OptionIconWrapper>
          <ReactCountryFlag
            countryCode={country}
            svg
            style={{
              width: '28px',
              height: '20px',
              borderRadius: '4px',
            }}
          />
        </OptionIconWrapper>
        <CountryCodeWrapper>
          +
          {isSupportedCountry(country || 'US') &&
            getCountryCallingCode(country)}
        </CountryCodeWrapper>
        <CountryName>{en[country]}</CountryName>
      </OptionWrapper>
    </components.Option>
  );
};

const CountryCodePicker = ({ ext, setExtension }: PhoneNumberInputProps) => {
  const themes = useTheme();

  const sortedCountries = React.useMemo(() => {
    const countries = getCountries();
    const filtered = countries.filter(c => c !== 'US' && c !== 'CA');
    filtered.sort((a, b) => en[a].localeCompare(en[b]));

    return [
      { value: 'US', label: en['US'] },
      { value: 'CA', label: en['CA'] },
      ...filtered.map(c => ({ value: c, label: en[c] })),
    ];
  }, []);
  const customStyles = {
    option: (base: React.CSSProperties): React.CSSProperties => {
      return {
        ...base,
        height: 48,
        display: 'flex',
        alignItems: 'center',
        padding: '0 16px',
        cursor: 'pointer',
        transition: 'all 0.3s',
        color: themes.colors.primary[100],
        backgroundColor: themes.colors.secondary[5],
      };
    },
    menu: (base: React.CSSProperties) => ({
      ...base,
      width: '320px',
    }),
  };

  return (
    <div style={{ minWidth: '120px' }}>
      <Select
        optionHeight={48}
        maxMenuHeight={220}
        menuShouldScrollIntoView={false}
        isLoading={false}
        components={{
          IndicatorSeparator: () => null,
          Option: CustomOption,
        }}
        formatOptionLabel={option => {
          const country = getCountries().find(
            country => country === option.value
          );
          if (!country) {
            return;
          }

          return (
            <OptionWrapper>
              <OptionIconWrapper>
                <ReactCountryFlag
                  countryCode={country}
                  svg
                  style={{
                    width: '28px',
                    height: '20px',
                    borderRadius: '4px',
                  }}
                />
              </OptionIconWrapper>
              <CountryCodeWrapper>
                +
                {isSupportedCountry(country || 'US') &&
                  getCountryCallingCode(country)}
              </CountryCodeWrapper>
            </OptionWrapper>
          );
        }}
        isSearchable={true}
        value={sortedCountries.find(option => option.value === ext)}
        options={sortedCountries}
        styles={customStyles}
        onChange={option => {
          if (option?.value) {
            setExtension(option.value as CountryCode);
          }
        }}
        onInputChange={(inputValue, { action }) => {
          if (action === 'input-change') {
            return inputValue.replace(/[^a-zA-Z\s]/g, '');
          }
          return inputValue;
        }}
        filterOption={(option, input) => {
          const isInvalidInput = /^[^a-zA-Z]+$/.test(input);
          if (isInvalidInput) return false;

          return option.label.toLowerCase().includes(input.toLowerCase());
        }}
      />
    </div>
  );
};

export default CountryCodePicker;
